import React, { useEffect, useState, useRef } from "react";
import { View, ScrollView, Pressable, Platform } from "react-native";
import tw from "twrnc";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import Text from "@/components/shared/AppText";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { text } from "@/components/settings/theme";
import { useEventAuth } from "@/components/teams/useWorkspaceAuth";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import PDFExportButton from "./PDFExportButton";
import { makeUrl } from "@/components/globalUtils";
import AppBrand from "@/components/shared/AppBrand";
import { videoStatsSimple } from "@/components/http/events";
import {
  ViewerHoursUsage,
  useEventViewerHours,
} from "@/components/viewerHours/EventViewerHours";
import EventReport from "@/components/viewerHours/EventReport";
import MuxReport from "@/components/viewerHours/MuxReport";
import {
  MUX_REPORT_ENABLED,
  MUX_REPORT_UNTIL,
} from "@/components/viewerHours/config";
import { ownReportStarted, reportSource } from "@/components/viewerHours/logic";
import { t } from "@/localization";

// Event Analytics (business accounts): our own report, kept for good
// (components/viewerHours/EventReport.js), under the videographer's
// viewer-hours card (not in the PDF). Events dated before the switch keep
// Mux's report while Mux runs (logic.js reportSource). MUX_REPORT_ENABLED
// builds show both for every event, Mux's at the end, for comparison.

const EventAnalytics = ({ event }) => {
  // Unique viewers (eventliveprod-event-viewers): undefined while loading.
  const [stats, setStats] = useState(undefined);
  // Teams: the account the event is in (yours, or the one you work in).
  const { broadcaster, custom_domain } = useEventAuth(event);
  const contentRef = useRef(null);
  const source = reportSource(event);
  const ownReport = source === "own" || MUX_REPORT_ENABLED;
  const muxReport = source === "mux" || MUX_REPORT_ENABLED;
  const viewerHours = useEventViewerHours(event, ownReport);

  useEffect(() => {
    if (!event || !broadcaster || !ownReport) return;
    let current = true;
    videoStatsSimple(event.broadcaster || broadcaster, event.slug).then(
      (s) => current && setStats(s || null)
    );
    return () => {
      current = false;
    };
  }, [event?.id, broadcaster, ownReport]);

  if (!event || (ownReport && (stats === undefined || viewerHours.loading))) {
    return <AppActivityIndicator />;
  }

  const url = makeUrl(event, custom_domain);

  return (
    <ScrollView style={tw`flex-1`}>
      <View>
        <SettingsPage
          title={t("Analytics")}
          help="analytics.overview"
          description={
            ownReport
              ? t("Updated every hour, for every player, and kept for good.")
              : undefined
          }
          right={<PDFExportButton event={event} contentRef={contentRef} />}
        />
        {ownReport ? (
          <ViewerHoursUsage
            event={event}
            state={viewerHours}
            style={tw`mb-6`}
          />
        ) : (
          <Callout tone="info" style={tw`mb-4 -mt-2`}>
            {ownReportStarted()
              ? t(
                  "This event keeps its current report until {date}. Then it moves to our new analytics, which count every player and are kept for good. Export a PDF to keep a copy of this report.",
                  {
                    date: moment.utc(MUX_REPORT_UNTIL).format(t("MMM D, YYYY")),
                  }
                )
              : t(
                  "Analytics are kept for 100 days. Export a PDF to keep a copy."
                )}
          </Callout>
        )}

        {/* The report (exported as the PDF) */}
        <View ref={contentRef} style={tw`bg-white p-4`}>
          <Text style={[text.sectionTitle, tw`mb-1`]}>{event.title}</Text>
          <View style={tw`mb-4`}>
            <Pressable
              onPress={() => {
                if (Platform.OS === "web") {
                  window.open(url, "_blank");
                }
              }}
              style={({ pressed }) => [
                tw`flex-row items-center`,
                { opacity: pressed ? 0.7 : 1 },
              ]}
            >
              <Text
                style={tw`text-[#F84123] underline font-bold`}
                numberOfLines={1}
              >
                {url.replace("https://", "")}
              </Text>
            </Pressable>
          </View>

          {ownReport && stats === null && (
            <Callout tone="danger" style={tw`mb-4`}>
              {t("Sorry, there's been an error loading the analytics data.")}
            </Callout>
          )}

          {ownReport && (
            <EventReport
              event={event}
              viewerHours={viewerHours}
              stats={stats}
            />
          )}

          {muxReport && <MuxReport event={event} comparison={ownReport} />}

          <View style={tw`flex-row items-center justify-center mt-4`}>
            <Text style={tw`text-gray-500 mr-2`}>{t("Powered by")}</Text>
            <AppBrand />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default EventAnalytics;
